// Vendor bill photo gallery — reuses the generic tbl_attachment table (see
// AttachmentSheet.tsx for the product-item equivalent) with entityTypeId = 6
// ("vendor bill", already registered in tbl_entityType). Images or PDFs, no
// cover-image concept — a plain gallery of however many bill files exist.
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { FileText, UploadCloud, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetPortal } from "ui/sheet";
import { Button } from "ui/button";
import { cn } from "lib/utils";
import { isArrayNotEmpty } from "../../Utils/helpers";
import { uploadPhotoToAWS, deleteFileFromS3 } from "../SubComponents/awsUpload";
import { createOrUpdateAttachmentAction, getVendorBillAttachmentAction } from "../../Store/actions/apparelAction";

const VENDOR_BILL_ENTITY_TYPE_ID = 6;
const VENDOR_BILL_S3_TYPE = 7;
const IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}vendor/bills/images/`;
const ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "pdf"];

const isPdf = (mimeType?: string, name?: string) =>
    mimeType === "application/pdf" || name?.split(".").pop()?.toLowerCase() === "pdf";

export interface VendorBillAttachmentSheetProps {
    open: boolean;
    onClose: () => void;
    entityId: any;
}

export function VendorBillAttachmentSheet({ open, onClose, entityId }: VendorBillAttachmentSheetProps) {
    const dispatch = useDispatch();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { getVendorBillAttachmentData, createOrUpdateAttachmentLoad } = apparelReducerState;
    const billImages = getVendorBillAttachmentData?.getVendorBillAttachment;

    const [imgList, setImgList] = useState<any[]>([]);
    const [refetchPending, setRefetchPending] = useState(false);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);

    const fetchBillAttachments = () => {
        if (entityId) dispatch(getVendorBillAttachmentAction({ entityId }));
    }

    useEffect(() => {
        if (open && entityId) fetchBillAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, entityId]);

    useEffect(() => {
        if (!open) setPreviewSrc(null);
    }, [open]);

    useEffect(() => {
        if (!previewSrc) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setPreviewSrc(null);
            }
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [previewSrc]);

    const imageUploadApi = (files: any, _coverImage: any, isDeleted: any) => {
        const obj = {
            attachmentId: "",
            fileName: files?.file?.name,
            entityId: entityId,
            fileUuid: files?.fileUuid,
            coverImage: 0,
            isImageOrVideo: VENDOR_BILL_ENTITY_TYPE_ID,
            mimeType: files?.mimeType ? files?.mimeType : files?.file?.type,
            fileSize: typeof files?.fileSize == "string" ? files?.fileSize : String(files?.file?.size ?? ""),
            isDeleted: isDeleted
        }
        dispatch(createOrUpdateAttachmentAction({ attachment: [obj] }));
        setRefetchPending(true);
    }

    const onPickImage = (file: File) => {
        if (imgList?.length >= 5) {
            toast.error("Please select up to 5 files only.");
            return;
        }
        const parts = file?.name?.split(".");
        const extension = parts?.[parts.length - 1]?.toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            toast.error("Only PNG, JPG and PDF files are allowed.");
            return;
        }
        const obj = { previewImage: URL.createObjectURL(file), file };
        setImgList((prev) => [...prev, obj]);
        // Upload immediately per file (not queued through singleImg state) so
        // selecting multiple files doesn't drop all but the last one to a
        // React state-batching race.
        uploadPhotoToAWS(obj, imageUploadApi, VENDOR_BILL_S3_TYPE);
    }

    useEffect(() => {
        if (refetchPending && !createOrUpdateAttachmentLoad) {
            fetchBillAttachments();
            setImgList([]);
            setRefetchPending(false);
            toast.success("Attachment updated successfully");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchPending, createOrUpdateAttachmentLoad]);

    const onRemoveImage = (item: any) => {
        deleteFileFromS3(item?.fileUuid, VENDOR_BILL_S3_TYPE);
        imageUploadApi({ fileUuid: item?.fileUuid, mimeType: item?.mimeType, fileSize: item?.fileSize, file: {} }, 0, 1);
    }

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Bill Attachment</SheetTitle>
                </SheetHeader>

                <div className="mt-4 space-y-4">
                    <UploadArea onFiles={(files) => files.forEach((f) => onPickImage(f))} />

                    <div className="flex flex-wrap gap-3">
                        {isArrayNotEmpty(imgList)
                            ? imgList.map((item: any, index: number) => {
                                const pdf = isPdf(item?.file?.type, item?.file?.name);
                                return (
                                    <ImageTile
                                        key={`local-${index}`}
                                        src={item?.previewImage}
                                        isPdf={pdf}
                                        showRemove={false}
                                        onRemove={() => {}}
                                        onView={() => (pdf ? window.open(item?.previewImage, "_blank") : setPreviewSrc(item?.previewImage))}
                                    />
                                );
                            })
                            : null}
                        {isArrayNotEmpty(billImages)
                            ? billImages.map((item: any, index: number) => {
                                const pdf = isPdf(item?.mimeType, item?.fileUuid);
                                const src = `${IMG_BASE}${item?.fileUuid}`;
                                return (
                                    <ImageTile
                                        key={`server-${index}`}
                                        src={src}
                                        isPdf={pdf}
                                        showRemove
                                        onRemove={() => onRemoveImage(item)}
                                        onView={() => (pdf ? window.open(src, "_blank") : setPreviewSrc(src))}
                                    />
                                );
                            })
                            : null}
                        {!isArrayNotEmpty(imgList) && !isArrayNotEmpty(billImages) && (
                            <p className="text-sm text-muted-foreground">No bill attachments uploaded yet.</p>
                        )}
                    </div>
                </div>
            </SheetContent>

            {previewSrc && (
                <SheetPortal>
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setPreviewSrc(null)}
                        onKeyDown={(e) => { if (e.key === "Escape") setPreviewSrc(null); }}
                        className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6"
                    >
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewSrc(null); }}
                            aria-label="Close preview"
                            className="absolute right-4 top-4 rounded-md bg-background/80 p-1.5 text-foreground hover:bg-background"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt, jsx-a11y/no-noninteractive-element-interactions */}
                        <img
                            src={previewSrc}
                            alt="bill attachment full size"
                            className="max-h-full max-w-full rounded-md object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </SheetPortal>
            )}
        </Sheet>
    );
}

function UploadArea({ onFiles }: { onFiles: (files: File[]) => void }) {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Bill Attachments</span>
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                <UploadCloud className="mr-1 h-4 w-4" />
                Upload
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.pdf"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files) onFiles(Array.from(e.target.files)); e.target.value = ""; }}
            />
        </div>
    );
}

function ImageTile({ src, isPdf, showRemove, onRemove, onView }: { src: string; isPdf?: boolean; showRemove: boolean; onRemove: () => void; onView: () => void }) {
    return (
        <div className={cn("relative h-24 w-24 overflow-hidden rounded-lg border")}>
            {isPdf ? (
                <button
                    type="button"
                    onClick={onView}
                    className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground"
                >
                    <FileText className="h-8 w-8" />
                    <span className="text-[11px] font-medium">PDF</span>
                </button>
            ) : (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img src={src} alt="bill attachment" className="h-full w-full cursor-pointer object-cover" onClick={onView} />
            )}
            {showRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove image"
                    className="absolute right-1 top-1 rounded-md bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}

export default VendorBillAttachmentSheet;
